/*! Snoozie Mini Game v1 | JS pur, aucune dépendance.
 *
 * Intégration : <div data-snoozie-minigame data-lang="fr"></div>
 * puis charger snoozie-minigame.css et snoozie-minigame.js.
 * Le jeu se monte seul. Il émet l'évènement "snoozie:minigame:win" avec
 * { clicks, percent, bonus } quand le chien est couché dans le lit.
 *
 * Trois couches dans ce fichier :
 *   1. le moteur (createEngine) : règles, minuterie, difficulté, sans DOM ;
 *   2. le rendu canvas (pixel art dessiné en rectangles) ;
 *   3. l'interface (mount) : HUD, écrans de départ et de fin, bilingue.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.SnoozieMiniGame = api;
    var go = function () { api.autoMount(); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go);
    else go();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Réglages : tout ce qui touche la difficulté vit ici                 */
  /* ------------------------------------------------------------------ */
  var CONFIG = {
    width: 240, height: 135,      // résolution logique du canvas (pixels "retro")
    floorY: 104,                  // ligne du plancher
    dogX: 168,                    // centre du chien (le lit arrive de la gauche)
    bedWidth: 64,
    speed: 480,                   // px logiques par seconde : le lit "défile très vite"
    speedJitter: 0.18,            // chaque passage varie de ±18 % : impossible à mémoriser
    speedStep: 0.05,              // +5 % de vitesse après chaque raté (jusqu'à speedStepMax ratés)
    speedStepMax: 4,
    lieDuration: 0.18,            // temps que le chien met à se coucher : il faut anticiper
    hitWindow: 5,                 // tolérance (px) entre le centre du lit et celui du chien
    almostWindow: 16,             // en deçà : message "Presque!"
    gap: [0.15, 0.55],            // pause aléatoire (s) entre deux lits
    firstBedDelay: 0.6,
    recoverTime: 0.8,             // le chien se relève après un raté
    bonusClicks: 5,               // 15 % si réussi en 5 clics ou moins
    bonusPercent: 15,
    basePercent: 10,
    mercyPerClick: 2,             // après le 5e clic le bonus est perdu : on élargit la
    mercyMax: 8,                  // tolérance pour que la partie se termine quand même
    // Niveaux : "set" écrase les réglages ci-dessus. Seul le niveau 0 donne un rabais,
    // les autres sont pour le plaisir. Gagner le niveau i débloque le niveau i + 1.
    levels: [
      { id: 'normal', set: {} },
      { id: 'hard', set: { speed: 600, hitWindow: 4, speedJitter: 0.22, speedStep: 0.03, mercyPerClick: 0 } },
      { id: 'impossible', set: { speed: 760, hitWindow: 1.5, speedJitter: 0.25, speedStep: 0, lieDuration: 0.14, mercyPerClick: 0 } }
    ]
  };

  /* ------------------------------------------------------------------ */
  /* 1. Moteur                                                           */
  /* ------------------------------------------------------------------ */
  function createEngine(userCfg, rng) {
    var base = Object.assign({}, CONFIG, userCfg || {});
    var cfg = Object.assign({}, base);
    rng = rng || Math.random;
    var s;

    function applyLevel(i) {
      var lv = base.levels[i] || base.levels[0];
      Object.assign(cfg, base, lv.set);   // cfg garde la même identité : le rendu voit le changement
    }

    function fresh(phase, level) {
      applyLevel(level || 0);
      s = {
        phase: phase,             // idle | playing | won
        level: level || 0,
        time: 0, clicks: 0, misses: 0, result: null, events: [],
        bed: { active: false, x: 0, v: 0, wait: cfg.firstBedDelay },
        dog: { pose: 'stand', t: 0, almost: false }   // stand | lie | fail | won
      };
    }
    fresh('idle');

    function rand(a, b) { return a + (b - a) * rng(); }

    function spawn() {
      var mult = 1 + cfg.speedStep * Math.min(s.misses, cfg.speedStepMax);
      var jitter = 1 + (rng() * 2 - 1) * cfg.speedJitter;
      s.bed.v = cfg.speed * mult * jitter;
      s.bed.x = -cfg.bedWidth / 2;
      s.bed.active = true;
    }

    function mercy() {
      return Math.min(cfg.mercyMax, Math.max(0, s.clicks - cfg.bonusClicks) * cfg.mercyPerClick);
    }

    function land() {
      var b = s.bed;
      var dx = b.active ? Math.abs(b.x - cfg.dogX) : Infinity;
      var m = mercy();
      if (dx <= cfg.hitWindow + m) {
        var bonus = s.level === 0 && s.clicks <= cfg.bonusClicks;
        s.phase = 'won';
        s.dog.pose = 'won';
        s.dog.t = 0;
        s.result = s.level === 0
          ? { level: 0, clicks: s.clicks, percent: bonus ? cfg.bonusPercent : cfg.basePercent, bonus: bonus }
          : { level: s.level, clicks: s.clicks, percent: null, bonus: false };
        s.events.push({ type: 'win', result: s.result });
      } else {
        s.misses++;
        s.dog.pose = 'fail';
        s.dog.t = 0;
        s.dog.almost = dx <= cfg.almostWindow + m;
        s.events.push({ type: 'miss', almost: s.dog.almost });
      }
    }

    function step(dt) {
      s.time += dt;
      var b = s.bed;
      if (s.phase === 'won') {
        b.x += (cfg.dogX - b.x) * Math.min(1, dt * 18);   // le lit se cale sous le chien
      } else if (b.active) {
        b.x += b.v * dt;
        if (b.x > cfg.width + cfg.bedWidth / 2) {
          b.active = false;
          b.wait = rand(cfg.gap[0], cfg.gap[1]);
        }
      } else {
        b.wait -= dt;
        if (b.wait <= 0) spawn();
      }
      var d = s.dog;
      d.t += dt;
      if (d.pose === 'lie' && d.t >= cfg.lieDuration) land();
      else if (d.pose === 'fail' && d.t >= cfg.recoverTime) { d.pose = 'stand'; d.t = 0; }
    }

    function click() {
      if (s.phase !== 'playing' || s.dog.pose !== 'stand') return false;
      s.clicks++;
      s.dog.pose = 'lie';
      s.dog.t = 0;
      s.events.push({ type: 'click', n: s.clicks });
      return true;
    }

    return {
      cfg: cfg,
      state: function () { return s; },
      start: function (level) { fresh('playing', level); },
      reset: function () { fresh('idle', 0); },
      click: click,
      step: step,
      drain: function () { var e = s.events; s.events = []; return e; }
    };
  }

  /* ------------------------------------------------------------------ */
  /* 2. Rendu pixel art                                                  */
  /* ------------------------------------------------------------------ */
  var C = {
    cream: '#FFF6E3', navy: '#1E2A4A', navyLight: '#2E3F6B',
    mint: '#8ED6B8', mintDark: '#6DBB9B', mintLight: '#CBEBDD',
    pink: '#EDB9A8', pinkDark: '#D99A86',
    tan: '#E0A96D', tanDark: '#B5763E', muzzle: '#F3DDB8'
  };

  function lerp(a, b, t) { return a + (b - a) * t; }
  function ease(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }

  function R(ctx, x, y, w, h, c) {
    ctx.fillStyle = c;
    ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
  }

  function bitmap(ctx, rows, x, y, scale, color) {
    ctx.fillStyle = color;
    for (var r = 0; r < rows.length; r++)
      for (var c = 0; c < rows[r].length; c++)
        if (rows[r][c] === 'x') ctx.fillRect(Math.round(x) + c * scale, Math.round(y) + r * scale, scale, scale);
  }
  var GLYPH_Z = ['xxxxx', '...x.', '..x..', '.x...', 'xxxxx'];
  var GLYPH_Q = ['xxx', '..x', '.x.', '...', '.x.'];
  var GLYPH_BANG = ['x', 'x', 'x', '.', 'x'];

  function drawBackground(ctx, cfg) {
    var W = cfg.width, H = cfg.height, FY = cfg.floorY;
    R(ctx, 0, 0, W, H, C.cream);
    R(ctx, 0, 0, W, 4, C.mint);
    // lambris menthe
    R(ctx, 0, 74, W, FY - 74, C.mint);
    R(ctx, 0, 74, W, 2, C.cream);
    R(ctx, 0, 76, W, 1, C.mintDark);
    for (var x = 12; x < W; x += 24) R(ctx, x, 77, 1, FY - 77, C.mintDark);
    R(ctx, 0, FY - 2, W, 2, C.navyLight);
    // plancher en damier
    for (var r = 0; FY + r * 8 < H; r++)
      for (var c = 0; c * 8 < W; c++)
        R(ctx, c * 8, FY + r * 8, 8, Math.min(8, H - (FY + r * 8)), (r + c) % 2 ? C.mintLight : C.navy);
    // fenêtre de nuit
    R(ctx, 21, 17, 46, 42, C.navy);
    R(ctx, 24, 20, 40, 36, C.navyLight);
    R(ctx, 43, 20, 2, 36, C.navy);
    R(ctx, 24, 36, 40, 2, C.navy);
    R(ctx, 18, 59, 52, 3, C.pink);
    // lune et étoiles
    R(ctx, 51, 24, 6, 8, C.cream); R(ctx, 49, 26, 10, 4, C.cream);
    R(ctx, 53, 24, 3, 2, C.navyLight); R(ctx, 54, 26, 5, 2, C.navyLight); R(ctx, 55, 28, 4, 3, C.navyLight);
    R(ctx, 29, 25, 1, 1, C.cream); R(ctx, 36, 30, 1, 1, C.cream); R(ctx, 30, 44, 1, 1, C.cream);
    R(ctx, 55, 46, 1, 1, C.cream); R(ctx, 36, 50, 1, 1, C.cream);
    // cadre avec patte
    R(ctx, 103, 21, 34, 30, C.navy);
    R(ctx, 105, 23, 30, 26, C.pink);
    R(ctx, 114, 35, 12, 7, C.cream); R(ctx, 116, 33, 8, 2, C.cream);
    R(ctx, 111, 29, 3, 4, C.cream); R(ctx, 116, 26, 3, 4, C.cream);
    R(ctx, 121, 26, 3, 4, C.cream); R(ctx, 126, 29, 3, 4, C.cream);
  }

  function drawBed(ctx, cfg, bedX, part) {
    var by = cfg.floorY - 1, x0 = Math.round(bedX) - 32;
    if (part === 'lip') {
      R(ctx, x0 - 1, by - 10, 66, 11, C.navy);
      R(ctx, x0, by - 9, 64, 9, C.pink);
      R(ctx, x0, by - 5, 64, 2, C.mint);
      return;
    }
    R(ctx, x0 - 1, by - 14, 66, 15, C.navy);          // contour
    R(ctx, x0 + 1, by - 13, 62, 4, C.cream);          // coussin
    R(ctx, x0 + 2, by - 11, 60, 2, C.creamDark || '#F1E2C3');
    R(ctx, x0, by - 9, 64, 9, C.pink);                // corps
    R(ctx, x0, by - 5, 64, 2, C.mint);                // liseré rétro
    R(ctx, x0, by - 1, 64, 1, C.pinkDark);
  }

  function dogParts(e, wag) {
    var L = function (a, b) { return Math.round(lerp(a, b, e)); };
    var legH = L(8, 0);
    var body = { x: L(-13, -14), y: L(-19, -10), w: L(24, 28), h: L(11, 10) };
    var head = { x: L(8, 12), y: L(-28, -13), w: 12, h: 10 };
    var p = [];
    if (legH > 0) {
      p.push({ x: -7, y: -legH, w: 3, h: legH, c: C.tanDark });
      p.push({ x: 2, y: -legH, w: 3, h: legH, c: C.tanDark });
    }
    p.push({ x: L(-17, -19), y: L(-21, -4), w: L(5, 6), h: L(4, 3), c: C.tan });
    if (e < 0.6) p.push({ x: -18 + wag, y: L(-21, -4) - 5, w: 3, h: 5, c: C.tan });
    p.push({ x: body.x, y: body.y, w: body.w, h: body.h, c: C.tan });
    if (legH > 0) {
      p.push({ x: -12, y: -legH, w: 4, h: legH, c: C.tan });
      p.push({ x: 6, y: -legH, w: 4, h: legH, c: C.tan });
    }
    if (e > 0.5) p.push({ x: 10, y: -3, w: 9, h: 3, c: C.tan });
    p.push({ x: head.x, y: head.y, w: head.w, h: head.h, c: C.tan });
    p.push({ x: head.x + 10, y: head.y + 4, w: 5, h: 5, c: C.muzzle });
    p.push({ x: head.x + 1, y: head.y + 1, w: 4, h: L(7, 6), c: C.tanDark });
    p.body = body; p.head = head;
    return p;
  }

  function dogLook(cfg, st, time) {
    var d = st.dog;
    if (d.pose === 'lie') return { e: ease(Math.min(1, d.t / cfg.lieDuration)), lift: 0, eye: 'open', wag: 0 };
    if (d.pose === 'won') return { e: 1, lift: Math.min(1, d.t / 0.15) * 8, eye: 'closed', wag: Math.floor(time * 8) % 2 };
    if (d.pose === 'fail') {
      var up = d.t < 0.45 ? 1 : Math.max(0, 1 - (d.t - 0.45) / (cfg.recoverTime - 0.45));
      return { e: ease(up), lift: 0, eye: d.t < 0.45 ? 'dazed' : 'open', wag: 0 };
    }
    return { e: 0, lift: 0, eye: 'open', wag: Math.floor(time * 4) % 2 };
  }

  function drawDog(ctx, cfg, st, time) {
    var look = dogLook(cfg, st, time);
    var parts = dogParts(look.e, look.wag), i, p;
    ctx.save();
    ctx.translate(cfg.dogX + 4, cfg.floorY - look.lift);   // le chien regarde vers la gauche : le lit arrive
    ctx.scale(-1, 1);
    for (i = 0; i < parts.length; i++) { p = parts[i]; R(ctx, p.x - 1, p.y - 1, p.w + 2, p.h + 2, C.navy); }
    for (i = 0; i < parts.length; i++) { p = parts[i]; R(ctx, p.x, p.y, p.w, p.h, p.c); }
    var hd = parts.head, bd = parts.body;
    R(ctx, bd.x + 6, bd.y, 8, 4, C.tanDark);                    // tache sur le dos
    R(ctx, hd.x + 13, hd.y + 4, 2, 2, C.navy);                  // truffe
    if (look.eye === 'open') R(ctx, hd.x + 6, hd.y + 3, 2, 2, C.navy);
    else if (look.eye === 'closed') R(ctx, hd.x + 5, hd.y + 4, 4, 1, C.navy);
    else {
      R(ctx, hd.x + 5, hd.y + 2, 1, 1, C.navy); R(ctx, hd.x + 7, hd.y + 2, 1, 1, C.navy);
      R(ctx, hd.x + 6, hd.y + 3, 1, 1, C.navy);
      R(ctx, hd.x + 5, hd.y + 4, 1, 1, C.navy); R(ctx, hd.x + 7, hd.y + 4, 1, 1, C.navy);
    }
    ctx.restore();
  }

  function draw(ctx, cfg, st, time, fx) {
    ctx.save();
    if (fx.shake > 0) ctx.translate(Math.floor(time * 60) % 2 ? 1 : -1, 0);
    drawBackground(ctx, cfg);
    if (st.bed.active) drawBed(ctx, cfg, st.bed.x, 'full');
    drawDog(ctx, cfg, st, time);
    if (st.phase === 'won' && st.bed.active) drawBed(ctx, cfg, st.bed.x, 'lip');
    var d = st.dog, FY = cfg.floorY;
    if (d.pose === 'fail' && d.t < 0.6) {
      var rise = Math.round(d.t * 10);
      if (d.almost) bitmap(ctx, GLYPH_BANG, cfg.dogX + 2, FY - 44 - rise, 2, C.navy);
      else bitmap(ctx, GLYPH_Q, cfg.dogX - 1, FY - 44 - rise, 2, C.navy);
    }
    if (d.pose === 'won' && d.t > 0.25) {
      for (var i = 0; i < 3; i++) {
        var ph = ((d.t * 0.7) + i / 3) % 1;
        bitmap(ctx, GLYPH_Z, cfg.dogX - 4 - i * 6 - ph * 6, FY - 26 - ph * 26, ph > 0.5 ? 2 : 1, C.navy);
      }
    }
    for (var k = 0; k < fx.parts.length; k++) {
      var q = fx.parts[k];
      R(ctx, q.x, q.y, q.size, q.size, q.c);
    }
    ctx.restore();
  }

  /* ------------------------------------------------------------------ */
  /* 3. Interface                                                        */
  /* ------------------------------------------------------------------ */
  var NB = ' ';
  var STRINGS = {
    en: {
      aria: 'Mini game: get the dog to lie down in the bed',
      bonus: 'Get him in the bed in 5 clicks or fewer for 15% off.',
      fun: 'Just for fun: your discount is already locked in.',
      clicks: 'Clicks',
      levelGroup: 'Level',
      levels: { normal: 'Normal', hard: 'Hard', impossible: 'Impossible' },
      locked: 'Win the previous level to unlock it',
      hint: 'Click, tap the screen or press Space to make the dog lie down.',
      intro: 'The bed moves fast. Time your click so the dog lands in it.',
      play: 'Play',
      almost: 'So close!', miss: 'Missed!',
      off: function (p) { return p + '% off'; },
      won: function (n, p) { return 'In ' + n + (n === 1 ? ' click' : ' clicks') + '! You unlocked ' + p + '% off.'; },
      nice: 'Nice!',
      wonAgain: function (n, p) { return 'In ' + n + (n === 1 ? ' click' : ' clicks') + '. Your discount stays at ' + p + '%.'; },
      cleared: function (name) { return name + ' cleared!'; },
      wonFun: function (n) { return 'In ' + n + (n === 1 ? ' click' : ' clicks') + '. Just for fun, your discount is unchanged.'; },
      wonTop: function (n) { return 'In ' + n + (n === 1 ? ' click' : ' clicks') + '. The dog is speechless.'; },
      unlocked: function (name) { return 'Level unlocked: ' + name; },
      next: function (name) { return 'Try ' + name; },
      code: 'Your code', replay: 'Play again',
      gateTitle: 'Enter your email to unlock your code',
      gateNote: 'This adds you to the Snoozie list: news and offers, unsubscribe anytime.',
      privacy: 'Privacy policy',
      emailLabel: 'Email address', emailPh: 'you@example.com',
      unlock: 'Unlock my code', sending: 'Sending...',
      errEmail: 'Please enter a valid email address.',
      errSend: 'We could not save your email. Please try again.',
      thanks: 'Thank you! Use this code at checkout.'
    },
    fr: {
      aria: 'Mini-jeu : faites coucher le chien dans son lit',
      bonus: 'Dans le lit en 5 clics ou moins, c’est 15' + NB + '% de rabais.',
      fun: 'Pour le plaisir : votre rabais est déjà acquis.',
      clicks: 'Clics',
      levelGroup: 'Niveau',
      levels: { normal: 'Normal', hard: 'Difficile', impossible: 'Impossible' },
      locked: 'Gagnez le niveau précédent pour le débloquer',
      hint: 'Cliquez, touchez l’écran ou appuyez sur Espace pour coucher le chien.',
      intro: 'Le lit passe très vite. Chronométrez votre clic pour que le chien tombe dedans.',
      play: 'Jouer',
      almost: 'Presque!', miss: 'Raté!',
      off: function (p) { return p + NB + '% de rabais'; },
      won: function (n, p) { return 'Dans le lit en ' + n + (n === 1 ? ' clic' : ' clics') + '! Rabais de ' + p + NB + '% débloqué.'; },
      nice: 'Bravo!',
      wonAgain: function (n, p) { return 'Dans le lit en ' + n + (n === 1 ? ' clic' : ' clics') + '. Votre rabais reste à ' + p + NB + '%.'; },
      cleared: function (name) { return name === 'Impossible' ? 'Impossible réussi!' : 'Niveau ' + name.toLowerCase() + ' réussi!'; },
      wonFun: function (n) { return 'En ' + n + (n === 1 ? ' clic' : ' clics') + '. Pour le plaisir, votre rabais ne change pas.'; },
      wonTop: function (n) { return 'En ' + n + (n === 1 ? ' clic' : ' clics') + '. Le chien est sans voix.'; },
      unlocked: function (name) { return 'Niveau débloqué' + NB + ': ' + name; },
      next: function (name) { return 'Essayer le niveau ' + name.toLowerCase(); },
      code: 'Votre code', replay: 'Rejouer',
      gateTitle: 'Entrez votre courriel pour débloquer votre code',
      gateNote: 'Vous serez ajouté à la liste Snoozie' + NB + ': nouveautés et offres, désabonnement en tout temps.',
      privacy: 'Politique de confidentialité',
      emailLabel: 'Adresse courriel', emailPh: 'vous@exemple.com',
      unlock: 'Débloquer mon code', sending: 'Envoi...',
      errEmail: 'Veuillez entrer une adresse courriel valide.',
      errSend: 'Nous n’avons pas pu enregistrer votre courriel. Veuillez réessayer.',
      thanks: 'Merci' + NB + '! Utilisez ce code à la caisse.'
    }
  };

  function h(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // Progression gardée dans le navigateur : niveau débloqué et rabais gagné à la PREMIÈRE victoire
  // du niveau normal. Les parties suivantes ne changent jamais le rabais (sinon on rejouerait
  // à l'infini jusqu'à obtenir le 15 %). Plus tard, ce verrou passera côté serveur, par courriel.
  var STORAGE_KEY = 'snoozie-minigame-v1';
  function loadProgress() {
    try {
      var r = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      if (r && typeof r === 'object') return { unlocked: r.unlocked | 0, earned: r.earned || null, emailDone: !!r.emailDone, pending: r.pending || null };
    } catch (e) { /* stockage bloqué : la progression vit le temps de la page */ }
    return { unlocked: 0, earned: null, emailDone: false, pending: null };
  }
  // Plusieurs onglets partagent ce stockage : un onglet resté ouvert garde en mémoire une progression périmée et
  // l'écrirait par-dessus celle d'un autre (courriel déjà donné redevenu « pas donné », donc le formulaire revenait
  // à chaque victoire). On fusionne donc avant d'écrire : ces trois états ne reculent jamais.
  function saveProgress(p) {
    try {
      var stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      if (stored && typeof stored === 'object') {
        if (stored.emailDone) { p.emailDone = true; p.pending = null; }
        if (stored.earned) p.earned = stored.earned;                       // le rabais reste celui de la première victoire
        if ((stored.unlocked | 0) > (p.unlocked | 0)) p.unlocked = stored.unlocked | 0;
      }
    } catch (e) { /* stockage illisible : on écrit ce qu'on a */ }
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch (e) { /* idem */ }
  }
  function resetProgress() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) { /* idem */ }
  }

  // Envoi du courriel : on remplit le VRAI formulaire client de Shopify (#snoozie-minigame-form, rendu par la section
  // Liquid) et on le soumet avec requestSubmit(), pour que la vérification anti-robot de Shopify (jeton CAPTCHA) se
  // déclenche comme sur n'importe quel formulaire du thème. Un formulaire fabriqué à la main échoue avec
  // « Missing CAPTCHA token ». Shopify enregistre le client (abonné aux courriels, étiquettes newsletter, minigame,
  // minigame-<rabais>) puis renvoie sur la page avec customer_posted=true : le jeu affiche alors le code (voir
  // `claim` plus bas). Sur localhost (aperçu), rien n'est envoyé.
  function isLocal() { return /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname); }
  // Le bootstrap CAPTCHA de Shopify annule (preventDefault) toute soumission d'un formulaire qui n'est pas encore lié à
  // hCaptcha, et ne le lie que sur un évènement focusin ou change venu du formulaire. Remplir les champs par
  // programmation ne déclenche ni l'un ni l'autre : sans ce focusin, requestSubmit() est annulé en silence et le joueur
  // voit « We could not save your email » après le délai. On le déclenche donc nous-mêmes et on attend le lien.
  function isBound(f) { return f.dataset && (f.dataset.hcaptchaBound === 'true' || f.dataset.recaptchaBound === 'true'); }
  function prepareShopifyForm(f) {
    var field = f && f.elements && f.elements['contact[email]'];
    if (!field || (f.dataset && isBound(f))) return;
    field.dispatchEvent(new Event('focusin', { bubbles: true }));
  }
  function submitShopifyForm(email, percent, f, bindTimeout) {
    f = f === undefined ? document.getElementById('snoozie-minigame-form') : f;
    if (!f || !f.elements || !f.elements['contact[email]'] || !f.elements['contact[tags]']) return Promise.resolve(false);
    f.elements['contact[email]'].value = email;
    f.elements['contact[tags]'].value = 'newsletter,minigame,minigame-' + percent;
    prepareShopifyForm(f);
    // Si le CAPTCHA est désactivé dans la boutique, le drapeau n'apparaît jamais : on soumet alors après le délai.
    var deadline = Date.now() + (bindTimeout || 4000);
    return new Promise(function (resolve) {
      (function wait() {
        if (isBound(f) || Date.now() >= deadline) {
          if (f.requestSubmit) f.requestSubmit(); else f.submit();
          resolve(true);
        } else setTimeout(wait, 50);
      })();
    });
  }
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  var PARTICLE_COLORS = [C.mint, C.pink, C.navy, C.cream, C.mintDark];

  function mount(el, opts) {
    opts = opts || {};
    var lang = (opts.lang || el.getAttribute('data-lang') || document.documentElement.lang || 'en').slice(0, 2);
    var T = STRINGS[lang] || STRINGS.en;
    if (!STRINGS[lang]) lang = 'en';
    var codes = Object.assign({ 10: 'SNOOZIE10', 15: 'SNOOZIE15' }, opts.codes || {});   // codes Shopify réels, affichés après le courriel
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var engine = createEngine(opts.config);
    var cfg = engine.cfg;
    var levels = cfg.levels;
    var progress = loadProgress();
    progress.unlocked = Math.min(progress.unlocked, levels.length - 1);
    var level = 0;

    function levelName(i) { return T.levels[levels[i].id] || levels[i].id; }

    el.textContent = '';
    el.classList.add('sm');

    var hud = h('div', 'sm-hud');
    var bonus = h('p', 'sm-bonus', T.bonus);
    hud.appendChild(bonus);
    var chipBox = h('div', 'sm-levels');
    chipBox.setAttribute('role', 'group');
    chipBox.setAttribute('aria-label', T.levelGroup);
    var chips = levels.map(function (lv, i) {
      var b = h('button', 'sm-chip', levelName(i));
      b.type = 'button';
      b.addEventListener('click', function () { selectLevel(i); });
      chipBox.appendChild(b);
      return b;
    });
    hud.appendChild(chipBox);
    var counter = h('div', 'sm-counter');
    counter.appendChild(h('span', 'sm-label', T.clicks));
    var pips = h('span', 'sm-pips');
    pips.setAttribute('aria-hidden', 'true');
    var pipEls = [];
    for (var i = 0; i < cfg.bonusClicks; i++) { var pe = h('i', 'sm-pip'); pipEls.push(pe); pips.appendChild(pe); }
    var count = h('b', 'sm-count', '0');
    counter.appendChild(pips);
    counter.appendChild(count);
    hud.appendChild(counter);

    var stage = h('div', 'sm-stage');
    var view = h('div', 'sm-view');
    view.tabIndex = 0;
    view.setAttribute('role', 'button');
    view.setAttribute('aria-label', T.aria);
    var canvas = document.createElement('canvas');
    view.appendChild(canvas);
    var toast = h('div', 'sm-toast');
    toast.setAttribute('role', 'status');
    view.appendChild(toast);
    stage.appendChild(view);

    var startOv = h('div', 'sm-overlay');
    var startCard = h('div', 'sm-card');
    startCard.appendChild(h('p', 'sm-intro', T.intro));
    var playBtn = h('button', 'sm-btn', T.play);
    playBtn.type = 'button';
    startCard.appendChild(playBtn);
    startOv.appendChild(startCard);
    stage.appendChild(startOv);

    var endOv = h('div', 'sm-overlay');
    endOv.hidden = true;
    stage.appendChild(endOv);

    var hint = h('p', 'sm-hint', T.hint);
    el.appendChild(hud);
    el.appendChild(stage);
    el.appendChild(hint);

    var ctx = canvas.getContext('2d');
    var fx = { shake: 0, parts: [] };

    function fit() {
      var dpr = window.devicePixelRatio || 1;
      var avail = view.clientWidth || cfg.width;
      var scale = Math.max(1, Math.ceil(avail * dpr / cfg.width));
      canvas.width = cfg.width * scale;
      canvas.height = cfg.height * scale;
      canvas.style.width = '100%';
      canvas.style.height = 'auto';
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      ctx.imageSmoothingEnabled = false;
    }

    function refreshHud() {
      var showBonus = level === 0 && !progress.earned;
      bonus.textContent = showBonus ? T.bonus : T.fun;
      pips.style.display = showBonus ? '' : 'none';
      chips.forEach(function (b, i) {
        var open = i <= progress.unlocked;
        b.disabled = !open;
        b.title = open ? '' : T.locked;
        b.classList.toggle('is-locked', !open);
        b.classList.toggle('is-on', i === level);
        b.setAttribute('aria-pressed', i === level ? 'true' : 'false');
      });
    }

    function resetCounter() {
      count.textContent = '0';
      pipEls.forEach(function (p) { p.className = 'sm-pip'; });
      pips.classList.remove('lost');
    }

    function flash(text) {
      toast.textContent = text;
      toast.classList.remove('on');
      void toast.offsetWidth;
      toast.classList.add('on');
    }

    function begin(lv) {
      if (typeof lv === 'number') level = lv;
      engine.start(level);
      fx.parts.length = 0;
      resetCounter();
      refreshHud();
      startOv.hidden = true;
      endOv.hidden = true;
      stage.classList.remove('is-end');
      view.focus({ preventScroll: true });
    }

    function selectLevel(i) {
      if (i > progress.unlocked) return;
      if (!startOv.hidden) { level = i; refreshHud(); }   // écran de départ : on choisit seulement
      else begin(i);
    }

    function codeRow(percent) {
      var row = h('p', 'sm-code');
      row.appendChild(h('span', 'sm-code-label', T.code));
      row.appendChild(h('code', 'sm-code-value', codes[percent]));
      return row;
    }

    // Le code n'apparaît qu'après le courriel. Le code reste dans ce fichier : l'écran le cache, il ne le protège pas.
    function emailGate(percent) {
      var form = h('form', 'sm-gate');
      form.noValidate = true;
      form.appendChild(h('p', 'sm-gate-title', T.gateTitle));
      var field = h('div', 'sm-gate-row');
      var id = 'sm-email-' + Math.random().toString(36).slice(2, 8);
      var label = h('label', 'sm-gate-label', T.emailLabel);
      label.setAttribute('for', id);
      var input = h('input', 'sm-gate-input');
      input.type = 'email'; input.id = id; input.name = 'email'; input.required = true;
      input.autocomplete = 'email'; input.setAttribute('inputmode', 'email');
      input.placeholder = T.emailPh;
      var go = h('button', 'sm-btn sm-gate-btn', T.unlock);
      go.type = 'submit';
      field.appendChild(label); field.appendChild(input); field.appendChild(go);
      form.appendChild(field);
      var trap = h('input', 'sm-trap');                       // pot de miel : un humain ne le voit pas
      trap.type = 'text'; trap.name = 'sm_website'; trap.tabIndex = -1; trap.autocomplete = 'off';
      trap.setAttribute('aria-hidden', 'true');
      form.appendChild(trap);
      var err = h('p', 'sm-gate-err');
      err.setAttribute('role', 'alert');
      form.appendChild(err);
      var note = h('p', 'sm-gate-note', T.gateNote + ' ');
      var link = h('a', null, T.privacy);
      link.href = (lang === 'fr' ? '/fr' : '') + '/policies/privacy-policy';
      link.target = '_blank'; link.rel = 'noopener';
      note.appendChild(link);
      form.appendChild(note);
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (trap.value) return;
        var email = input.value.trim();
        if (!EMAIL_RE.test(email)) { err.textContent = T.errEmail; input.setAttribute('aria-invalid', 'true'); input.focus(); return; }
        err.textContent = ''; input.removeAttribute('aria-invalid');
        go.disabled = true; input.disabled = true; go.textContent = T.sending;
        function fail() {
          go.disabled = false; input.disabled = false; go.textContent = T.unlock;
          err.textContent = T.errSend;
        }
        function done() {
          progress.emailDone = true;
          saveProgress(progress);
          var ok = document.createDocumentFragment();
          ok.appendChild(h('p', 'sm-msg', T.thanks));
          ok.appendChild(codeRow(percent));
          form.parentNode.replaceChild(ok, form);
          el.dispatchEvent(new CustomEvent('snoozie:minigame:email', { bubbles: true, detail: { percent: percent } }));
        }
        if (!opts.submitEmail && !isLocal()) {
          // Vraie boutique : soumission du formulaire Shopify, la page se recharge puis le code s'affiche (claim)
          progress.pending = { percent: percent };
          saveProgress(progress);
          submitShopifyForm(email, percent).then(function (sent) {
            if (!sent) { progress.pending = null; saveProgress(progress); fail(); }
          });
          setTimeout(fail, 20000);   // défi fermé ou envoi bloqué : on rend la main au joueur
          return;
        }
        // Aperçu local ou test : envoi simulé sur place
        Promise.resolve().then(function () { return opts.submitEmail ? opts.submitEmail(email, percent) : new Promise(function (ok) { setTimeout(ok, 400); }); }).then(done, fail);
      });
      // hCaptcha se charge pendant que la personne tape son adresse (voir prepareShopifyForm)
      if (!opts.submitEmail && !isLocal()) prepareShopifyForm(document.getElementById('snoozie-minigame-form'));
      return form;
    }

    // Retour d'un envoi par page complète : Shopify ajoute customer_posted=true à l'adresse quand le client est enregistré.
    function showClaimed(percent) {
      startOv.hidden = true;
      endOv.textContent = '';
      var card = h('div', 'sm-card');
      card.appendChild(h('div', 'sm-big', T.off(percent)));
      card.appendChild(h('p', 'sm-msg', T.thanks));
      card.appendChild(codeRow(percent));
      var btns = h('div', 'sm-actions');
      var again = h('button', 'sm-btn', T.replay);
      again.type = 'button';
      again.addEventListener('click', function () { begin(0); });
      btns.appendChild(again);
      card.appendChild(btns);
      endOv.appendChild(card);
      endOv.hidden = false;
      stage.classList.add('is-end');
    }

    function showEnd(r, flags) {
      flags = flags || {};
      var earned = progress.earned || { percent: r.percent || cfg.bonusPercent };
      var top = levels.length - 1;
      var big, msg;
      if (r.level === 0) {
        big = flags.firstWin ? T.off(earned.percent) : T.nice;
        msg = flags.firstWin ? T.won(r.clicks, earned.percent) : T.wonAgain(r.clicks, earned.percent);
      } else {
        big = T.cleared(levelName(r.level));
        msg = r.level === top ? T.wonTop(r.clicks) : T.wonFun(r.clicks);
      }
      endOv.textContent = '';
      var card = h('div', 'sm-card');
      card.appendChild(h('div', 'sm-big', big));
      card.appendChild(h('p', 'sm-msg', msg));
      if (flags.unlockedNow != null) card.appendChild(h('p', 'sm-unlock', T.unlocked(levelName(flags.unlockedNow))));
      if (progress.emailDone) card.appendChild(codeRow(earned.percent));
      else card.appendChild(emailGate(earned.percent));
      var btns = h('div', 'sm-actions');
      var again = h('button', 'sm-btn', T.replay);
      again.type = 'button';
      again.addEventListener('click', function () { begin(r.level); });
      btns.appendChild(again);
      if (r.level + 1 < levels.length && r.level + 1 <= progress.unlocked) {
        var nxt = h('button', 'sm-btn sm-btn--alt', T.next(levelName(r.level + 1)));
        nxt.type = 'button';
        nxt.addEventListener('click', function () { begin(r.level + 1); });
        btns.appendChild(nxt);
      }
      card.appendChild(btns);
      endOv.appendChild(card);
      endOv.hidden = false;
      stage.classList.add('is-end');
    }

    // Enregistre la progression et renvoie ce qui vient de se passer.
    function recordWin(r) {
      var flags = { firstWin: false, unlockedNow: null };
      if (r.level === 0 && !progress.earned) {
        progress.earned = { percent: r.percent, clicks: r.clicks };
        flags.firstWin = true;
      }
      if (r.level + 1 < levels.length && progress.unlocked < r.level + 1) {
        progress.unlocked = r.level + 1;
        flags.unlockedNow = r.level + 1;
      }
      saveProgress(progress);
      refreshHud();
      return flags;
    }

    function onEvent(ev) {
      if (ev.type === 'click') {
        count.textContent = String(ev.n);
        if (level === 0) {
          if (ev.n <= cfg.bonusClicks) pipEls[ev.n - 1].classList.add('used');
          else pips.classList.add('lost');
        }
      } else if (ev.type === 'miss') {
        flash(ev.almost ? T.almost : T.miss);
        if (!reduced) fx.shake = 0.18;
      } else if (ev.type === 'win') {
        var st = engine.state();
        if (!reduced) {
          for (var k = 0; k < 40; k++) {
            fx.parts.push({
              x: st.bed.x, y: cfg.floorY - 16,
              vx: (Math.random() * 2 - 1) * 70, vy: -40 - Math.random() * 110,
              life: 1.2 + Math.random() * 0.5,
              size: Math.random() < 0.5 ? 2 : 1,
              c: PARTICLE_COLORS[k % PARTICLE_COLORS.length]
            });
          }
        }
        var flags = recordWin(ev.result);
        var detail = {
          level: ev.result.level, clicks: ev.result.clicks,
          firstWin: flags.firstWin,
          percent: progress.earned ? progress.earned.percent : null   // le rabais VERROUILLÉ, pas celui de cette partie
        };
        setTimeout(function () { showEnd(ev.result, flags); }, 1000);
        if (opts.onWin) opts.onWin(detail);
        el.dispatchEvent(new CustomEvent('snoozie:minigame:win', { bubbles: true, detail: detail }));
      }
    }

    function updateFx(dt) {
      if (fx.shake > 0) fx.shake -= dt;
      for (var k = fx.parts.length - 1; k >= 0; k--) {
        var q = fx.parts[k];
        q.life -= dt;
        if (q.life <= 0) { fx.parts.splice(k, 1); continue; }
        q.vy += 260 * dt;
        q.x += q.vx * dt;
        q.y += q.vy * dt;
      }
    }

    view.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button > 0) return;
      engine.click();
    });
    view.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); engine.click(); }
    });
    playBtn.addEventListener('click', function () { begin(level); });

    var raf = 0, last = 0;
    function frame(ts) {
      raf = requestAnimationFrame(frame);
      var dt = last ? Math.min(0.033, (ts - last) / 1000) : 0;
      last = ts;
      engine.step(dt);
      var evs = engine.drain();
      for (var k = 0; k < evs.length; k++) onEvent(evs[k]);
      updateFx(dt);
      draw(ctx, cfg, engine.state(), engine.state().time, fx);
    }
    function run() { if (!raf) { last = 0; raf = requestAnimationFrame(frame); } }
    function halt() { if (raf) { cancelAnimationFrame(raf); raf = 0; } }

    if (window.ResizeObserver) new ResizeObserver(fit).observe(view);
    else window.addEventListener('resize', fit);
    fit();
    refreshHud();
    var posted = /[?&]customer_posted=true(&|$)/.test(window.location.search);
    if (posted && progress.pending && progress.earned) {
      var claimedPercent = progress.earned.percent;
      progress.emailDone = true; progress.pending = null;
      saveProgress(progress);
      try { window.history.replaceState(null, '', window.location.pathname + window.location.hash); } catch (e) { /* adresse laissée telle quelle */ }
      showClaimed(claimedPercent);
      el.dispatchEvent(new CustomEvent('snoozie:minigame:email', { bubbles: true, detail: { percent: claimedPercent } }));
    }
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) { entries[0].isIntersecting ? run() : halt(); }).observe(el);
    }
    run();

    return {
      engine: engine,
      restart: begin,
      progress: function () { return progress; },
      showResult: function (r, flags) { showEnd(r, flags); }
    };
  }

  function autoMount() {
    var nodes = document.querySelectorAll('[data-snoozie-minigame]');
    for (var i = 0; i < nodes.length; i++)
      if (!nodes[i].classList.contains('sm')) mount(nodes[i]);
  }

  return { CONFIG: CONFIG, createEngine: createEngine, mount: mount, autoMount: autoMount, resetProgress: resetProgress, loadProgress: loadProgress, saveProgress: saveProgress, submitShopifyForm: submitShopifyForm };
});
